/**
 * MermaidBlock — renders a mermaid diagram inline.
 *
 * Mermaid (~300 KB gzipped) is lazy-imported on first mount via
 * `await import("mermaid")` so the main chat bundle stays unchanged
 * until a diagram actually renders. `mermaid.initialize` runs once
 * per page load (module-level guard).
 *
 * Used by the `render_diagram` tool block and by the markdown-fence
 * fallback (`language-mermaid`). The two callers pass distinct `id`s
 * so `mermaid.render` doesn't clobber one SVG with another.
 */

import { Expand } from "lucide-react";
import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";

import { MermaidLightbox } from "@/components/chat/MermaidLightbox";
import { useTheme } from "@/lib/theme-context";

// Sober BeFlow-palette theme overrides. Drives a unified look across
// flowcharts and sequence diagrams instead of letting the LLM's
// classDef fills paint each node a different bright colour. Colours
// come from `tailwind.config.ts -> brand.primary / brand.secondary`.
const DARK_THEME = {
  primaryColor: "#2d3d76", // brand.primary.01 — node fill
  primaryTextColor: "#e4e8ec", // brand.secondary.40 — label text
  primaryBorderColor: "#7998a7", // brand.primary.20 — node border
  secondaryColor: "#536d8f", // brand.primary.10
  secondaryTextColor: "#e4e8ec",
  secondaryBorderColor: "#9ebcc0", // brand.primary.30
  tertiaryColor: "#41515e", // brand.extra.2-10
  tertiaryTextColor: "#e4e8ec",
  tertiaryBorderColor: "#7998a7",
  lineColor: "#9ebcc0",
  textColor: "#e4e8ec",
  background: "transparent",
  // Sequence diagrams
  actorBkg: "#2d3d76",
  actorBorder: "#7998a7",
  actorTextColor: "#e4e8ec",
  actorLineColor: "#7998a7",
  signalColor: "#9ebcc0",
  signalTextColor: "#e4e8ec",
  labelBoxBkgColor: "#41515e",
  labelBoxBorderColor: "#7998a7",
  labelTextColor: "#e4e8ec",
  loopTextColor: "#e4e8ec",
  noteBkgColor: "#41515e",
  noteTextColor: "#e4e8ec",
  noteBorderColor: "#7998a7",
};

const LIGHT_THEME = {
  primaryColor: "#dce5ec", // brand.secondary.30 — node fill
  primaryTextColor: "#142c4a", // brand.primary.00 — label text
  primaryBorderColor: "#536d8f",
  secondaryColor: "#e4e8ec",
  secondaryTextColor: "#142c4a",
  secondaryBorderColor: "#7998a7",
  tertiaryColor: "#f5f5f5", // brand.neutral.20
  tertiaryTextColor: "#142c4a",
  tertiaryBorderColor: "#7998a7",
  lineColor: "#536d8f",
  textColor: "#142c4a",
  background: "transparent",
  actorBkg: "#dce5ec",
  actorBorder: "#536d8f",
  actorTextColor: "#142c4a",
  actorLineColor: "#536d8f",
  signalColor: "#536d8f",
  signalTextColor: "#142c4a",
  labelBoxBkgColor: "#f5f5f5",
  labelBoxBorderColor: "#7998a7",
  labelTextColor: "#142c4a",
  loopTextColor: "#142c4a",
  noteBkgColor: "#f5f5f5",
  noteTextColor: "#142c4a",
  noteBorderColor: "#7998a7",
};

/** Map the active app theme to the existing Light or Navy diagram palette. */
function paletteFor(theme: string): typeof DARK_THEME {
  if (theme === "light") return LIGHT_THEME;
  return DARK_THEME;
}

// Sequence-diagram colour overrides (mermaid v11 hardcodes `fill="#eaeaea"` /
// `stroke="#666"` as SVG ATTRIBUTES on actor rects, bypassing themeVariables;
// CSS `!important` wins over presentation attributes). The foreignObject
// overflow rules are theme-independent; the colour rules track the palette.
const FO_OVERFLOW_RULES =
  "svg[aria-roledescription] foreignObject{overflow:visible!important;}" +
  "svg[aria-roledescription] foreignObject > *{overflow:visible!important;max-width:none!important;}";

function sequenceColorRules(theme: string): string {
  const p = paletteFor(theme);
  return (
    `svg[aria-roledescription="sequence"] rect.actor{fill:${p.actorBkg}!important;stroke:${p.actorBorder}!important;}` +
    `svg[aria-roledescription="sequence"] line.actor-line{stroke:${p.actorLineColor}!important;}` +
    `svg[aria-roledescription="sequence"] text.actor{fill:${p.actorTextColor}!important;}` +
    `svg[aria-roledescription="sequence"] .messageLine0,svg[aria-roledescription="sequence"] .messageLine1{stroke:${p.signalColor}!important;}` +
    `svg[aria-roledescription="sequence"] text.messageText{fill:${p.signalTextColor}!important;}` +
    `svg[aria-roledescription="sequence"] rect.note{fill:${p.noteBkgColor}!important;stroke:${p.noteBorderColor}!important;}` +
    `svg[aria-roledescription="sequence"] text.noteText{fill:${p.noteTextColor}!important;}`
  );
}

/** Strip the LLM's per-node colour customisation so the unified theme
 * wins. Mermaid offers three colouring surfaces — we neutralise all
 * three:
 *   - `classDef Foo fill:#…,stroke:#…;` declarations (the class form).
 *   - `:::Foo` style assignments on individual nodes.
 *   - `style NodeName fill:#…,stroke:#…,color:#…` direct directives.
 * Topology (nodes, edges, labels) is preserved unchanged. */
function stripClassDef(code: string): string {
  return code
    .split("\n")
    .filter((line) => !/^\s*classDef\s/i.test(line))
    .filter((line) => !/^\s*style\s+\S+\s+(fill|stroke|color)/i.test(line))
    .map((line) => line.replace(/:::\s*[\w-]+/g, ""))
    .join("\n");
}

interface MermaidBlockProps {
  code: string;
  id: string;
  /** Fires once when the SVG is mounted in the DOM, with the wall-time
   * spent from effect start (lazy `import("mermaid")` + `initialize` +
   * `render` + `fixForeignObjectWidths`) to ready. The `render_diagram`
   * step row uses this to replace the near-zero backend shape-check
   * timing with what the operator actually waited for. Held in a ref
   * inside so changing the callback identity across renders doesn't
   * re-trigger the heavy mermaid pass. */
  onRendered?: (renderMs: number) => void;
}

export function MermaidBlock({ code, id, onRendered }: MermaidBlockProps): JSX.Element {
  const [svg, setSvg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  // Read the theme reactively from context (not the DOM): switching themes
  // re-runs the render effect below so the diagram re-colours instead of
  // keeping its baked-in palette. Using context avoids a render-order race
  // with ThemeProvider's `data-theme` write.
  const { theme } = useTheme();
  // Hold `onRendered` in a ref so the effect below can read the latest
  // closure without listing it in its deps — `mermaid.render` is
  // expensive and shouldn't re-run just because the caller passed a
  // fresh inline function.
  const onRenderedRef = useRef(onRendered);
  useEffect(() => {
    onRenderedRef.current = onRendered;
  }, [onRendered]);

  useEffect(() => {
    let cancelled = false;
    const renderStartedAt = performance.now();
    void (async () => {
      try {
        const mermaid = (await import("mermaid")).default;
        // Re-initialise on every render: mermaid v11 sometimes drops the
        // sequence-diagram-specific theme variables across diagram-type
        // changes (a flowchart render before a sequence render resets
        // `actorBkg` etc. back to its hardcoded `#eaeaea` default). Cheap
        // — `initialize` is a config setter, not a renderer.
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: "strict",
          theme: "base",
          themeVariables: paletteFor(theme),
          // Without this, a parse/draw failure makes mermaid render its
          // native "Syntax error in text" bomb SVG into a temp element it
          // appends to `document.body`, then re-throws — and the throw
          // skips the end-of-render cleanup, so the orphan bomb is left on
          // the body and stacks up (one per failed render). We handle the
          // throw with our own Spanish fallback below, so suppress mermaid's
          // graphic entirely: on error it now just removes its temp element
          // and throws cleanly, leaking nothing into the DOM.
          suppressErrorRendering: true,
        });
        const cleaned = stripClassDef(code);
        // Validate before rendering: a body that fails `parse` throws here,
        // deterministically hitting the catch below without ever reaching
        // `render`. Defense-in-depth for the fence fallback path — once
        // lever 1 (deferred rendering while streaming) hands this effect a
        // complete body, `render`'s own catch already yields the same
        // fallback, but the explicit gate makes the invalid path
        // testable and consistent with the tool path's shape check.
        await mermaid.parse(cleaned);
        const result = await mermaid.render(id, cleaned);
        if (!cancelled) {
          // Mermaid 11 measures HTML labels with metrics that sometimes
          // undersize the `<foreignObject>` width — text like "Routed"
          // ends up rendered as "Routec" because the FO acts as a hard
          // clip on the inner HTML. Re-measure each FO after the SVG
          // is mounted off-screen and expand its declared width when
          // the natural content overflows.
          const fixed = await fixForeignObjectWidths(result.svg);
          setSvg(fixed);
          setError(null);
          onRenderedRef.current?.(performance.now() - renderStartedAt);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : String(err);
          setSvg(null);
          setError(message);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code, id, theme]);

  if (error !== null) {
    return (
      <div className="my-2">
        <div className="text-body-3 text-destructive">
          No pude renderizar el diagrama. Puede que tenga un error de sintaxis.
        </div>
        <details className="mt-1">
          <summary className="cursor-pointer text-body-3 text-muted-foreground">
            Ver código mermaid
          </summary>
          <pre className="mt-1 overflow-x-auto rounded bg-muted p-2 font-mono text-body-3">
            {code}
          </pre>
          <div className="mt-1 text-body-3 text-muted-foreground">Detalle técnico: {error}</div>
        </details>
      </div>
    );
  }

  if (svg === null) {
    return (
      <div className="my-2 text-body-3 italic text-muted-foreground">renderizando diagrama…</div>
    );
  }

  return (
    <>
      {/* (1) `overflow: visible` on foreignObject — defense-in-depth
          against label clipping when mermaid's measurement undersizes
          the FO width (font cascade mismatch in offscreen measurement).
          (2) Sequence-diagram colour overrides — mermaid v11 hardcodes
          `fill="#eaeaea"` / `stroke="#666"` as SVG ATTRIBUTES on actor
          rects, bypassing the theme variables. CSS with !important
          wins over SVG presentation attributes, so we force the same
          sober BeFlow palette here. */}
      <style>{FO_OVERFLOW_RULES + sequenceColorRules(theme)}</style>
      <div className="relative my-2 max-h-[32rem] overflow-y-auto rounded border border-border bg-background p-2">
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          // Pinned to the top-right corner of the card. The diagram below
          // already scrolls inside `max-h-[32rem]`, so the button stays in
          // place on tall diagrams. `bg-background/80` keeps it readable
          // when the SVG content overlaps the corner.
          className="absolute right-2 top-2 z-10 rounded border border-border bg-background/80 p-1 text-muted-foreground backdrop-blur-sm hover:bg-muted hover:text-foreground"
          title="Abrir en pantalla completa"
          aria-label="Abrir en pantalla completa"
        >
          <Expand className="h-3.5 w-3.5" />
        </button>
        {/* oxlint-disable react-doctor/no-danger -- trusted SVG output from mermaid (see biome-ignore on the dangerouslySetInnerHTML line) */}
        <div
          // mermaid.render returns trusted SVG (we set `securityLevel: 'strict'`
          // on init, which strips inline event handlers and `javascript:` URLs
          // from labels). The `code` input is already shape-checked by the
          // backend `render_diagram` handler.
          // biome-ignore lint/security/noDangerouslySetInnerHtml: trusted SVG output from mermaid
          dangerouslySetInnerHTML={{ __html: svg }}
        />
        {/* oxlint-enable react-doctor/no-danger */}
      </div>
      {lightboxOpen && <MermaidLightbox svg={svg} onClose={() => setLightboxOpen(false)} />}
    </>
  );
}

/**
 * Mount the SVG markup in an off-screen container, measure each
 * `<foreignObject>`'s natural content width, and rewrite the FO's
 * `width` attribute when the content overflows. Returns the corrected
 * markup. Without this fix, labels like "Routed" can render as
 * "Routec" because mermaid's HTML-label measurement undersizes the FO
 * for some font/zoom combinations.
 */
async function fixForeignObjectWidths(svgMarkup: string): Promise<string> {
  const container = document.createElement("div");
  container.style.cssText = "position: absolute; left: -9999px; top: -9999px; visibility: hidden;";
  container.innerHTML = svgMarkup;
  document.body.appendChild(container);
  try {
    // Wait one frame so the browser lays out the inner HTML before we
    // measure scrollWidth.
    await new Promise<void>((r) => requestAnimationFrame(() => r()));
    const fos = container.querySelectorAll("foreignObject");
    for (const fo of Array.from(fos)) {
      const child = fo.firstElementChild;
      if (!(child instanceof HTMLElement)) continue;
      const declared = Number.parseFloat(fo.getAttribute("width") ?? "0");
      const natural = child.scrollWidth;
      if (natural > declared) {
        // Buffer covers (a) sub-pixel rounding, (b) the fact that the
        // offscreen measurement may use slightly different font metrics
        // than the in-tree render (CSS cascade differences). 12 px is
        // empirically enough to keep the labels from clipping while
        // staying invisible against the node's rounded background.
        fo.setAttribute("width", String(Math.ceil(natural) + 12));
      }
    }
    const fixed = container.querySelector("svg");
    return fixed?.outerHTML ?? svgMarkup;
  } finally {
    document.body.removeChild(container);
  }
}
